import { useEffect, useMemo, useState } from "react";
import { X, Copy, Check, ExternalLink, Loader2, QrCode, Monitor, MessageCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard, buildWhatsAppApiUrl } from "@/lib/whatsapp";

/**
 * PayTR Ödeme Linki + Iframe Modalı — dükkanın kendi PayTR anahtarları üzerinden.
 *
 * İki mod:
 *   • QR / Link — müşteri kendi telefonundan öder (masaüstünde en pratik yol)
 *   • Kasada Öde — iframe modalda müşteri kart bilgilerini kasada girer
 *
 * Data-testids:
 *   paytr-link-modal, paytr-mode-link, paytr-mode-iframe, paytr-copy-link,
 *   paytr-open-link, paytr-send-wa, paytr-close, paytr-iframe
 */
export default function PayTRLinkModal({
  open,
  amount,
  customerName,
  customerPhone,
  data,          // { oid, amount_tl, iframe_url, token }
  loading,
  error,
  onClose,
  onRefresh,
}) {
  const [mode, setMode] = useState("link");   // "link" | "iframe"
  const [copied, setCopied] = useState(false);

  const url = data?.iframe_url || "";
  const qrUrl = useMemo(
    () => url ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&bgcolor=0a0a0a&color=d4af37&format=png&data=${encodeURIComponent(url)}` : "",
    [url]
  );

  useEffect(() => {
    if (!open) { setCopied(false); setMode("link"); }
  }, [open]);

  if (!open) return null;

  const onCopy = async () => {
    const ok = await copyToClipboard(url);
    setCopied(ok);
    if (ok) toast.success("Ödeme linki panoya kopyalandı");
  };

  const onSendWA = () => {
    if (!customerPhone) return toast.error("Müşteri telefon numarası yok");
    const clean = String(customerPhone).replace(/\D/g, "");
    const p = clean.startsWith("90") ? clean : `90${clean.replace(/^0/, "")}`;
    const msg = `Merhaba ${customerName || ""}, satış tutarınız *${(amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺* için ödeme linkiniz:\n${url}\n\nGüvenli ödeme · PayTR · MİLYEMSOFT`;
    const wa = buildWhatsAppApiUrl(p, msg);
    window.open(wa, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp açıldı");
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="paytr-link-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl card-flat border relative"
        style={{ borderColor: "rgba(212,175,55,0.5)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "var(--gold)" }}>
              <span className="text-black font-bold text-[10px] tracking-tight">PayTR</span>
            </div>
            <div>
              <div className="text-sm font-semibold">Online Ödeme</div>
              <div className="label-caps text-[10px]" style={{ color: "var(--gold)" }}>
                {amount ? `${amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺` : "—"} · Kendi Altyapın
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Kapat"
            data-testid="paytr-close"
            className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-red-400 hover:text-red-300 transition"
          >
            <X size={16} />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-0 border-b border-white/5">
          <button
            type="button"
            onClick={() => setMode("link")}
            data-testid="paytr-mode-link"
            className={`flex items-center justify-center gap-1.5 py-2.5 text-xs transition ${mode === "link" ? "text-black font-semibold" : "text-neutral-400 hover:text-white"}`}
            style={mode === "link" ? { background: "var(--gold)" } : undefined}
          >
            <QrCode size={12} /> Link / QR — Müşteri Öder
          </button>
          <button
            type="button"
            onClick={() => setMode("iframe")}
            data-testid="paytr-mode-iframe"
            className={`flex items-center justify-center gap-1.5 py-2.5 text-xs transition ${mode === "iframe" ? "text-black font-semibold" : "text-neutral-400 hover:text-white"}`}
            style={mode === "iframe" ? { background: "var(--gold)" } : undefined}
          >
            <Monitor size={12} /> Kasada Öde — Iframe
          </button>
        </div>

        <div className="p-4 sm:p-5">
          {loading && (
            <div className="p-12 flex flex-col items-center gap-3 text-neutral-500">
              <Loader2 size={22} className="animate-spin" />
              <div className="text-xs">PayTR bağlanıyor, saniyeler…</div>
            </div>
          )}

          {!loading && error && (
            <div className="p-6 text-center">
              <div className="text-sm text-red-400 mb-3">{error}</div>
              <button onClick={onRefresh} className="btn-gold inline-flex items-center gap-2 px-4 py-2 text-xs">
                <RefreshCw size={12} /> Tekrar Dene
              </button>
            </div>
          )}

          {!loading && !error && url && mode === "link" && (
            <div className="grid md:grid-cols-[240px_1fr] gap-5 items-center">
              <div className="flex flex-col items-center gap-2">
                <img src={qrUrl} alt="Ödeme QR" width={240} height={240} className="border border-white/10" />
                <div className="text-[10px] text-neutral-500 tracking-widest">KAMERA İLE OKUT</div>
              </div>

              <div className="space-y-3 min-w-0">
                <div>
                  <div className="label-caps text-[10px] mb-1.5">Ödeme Linki</div>
                  <textarea
                    readOnly
                    value={url}
                    rows={3}
                    className="w-full bg-black/40 border border-white/10 px-3 py-2 text-[11px] font-mono leading-relaxed text-neutral-300 focus:outline-none focus:border-[--gold] resize-none break-all"
                    data-testid="paytr-link-value"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={onCopy}
                    data-testid="paytr-copy-link"
                    className={`flex items-center justify-center gap-2 border px-3 py-2.5 text-sm transition active:scale-95 ${copied ? "border-emerald-400 text-emerald-300" : "border-white/15 hover:border-[--gold] hover:text-[--gold]"}`}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? "Kopyalandı" : "Linki Kopyala"}
                  </button>
                  <button
                    onClick={onSendWA}
                    data-testid="paytr-send-wa"
                    className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm text-black font-semibold transition active:scale-95 hover:brightness-110"
                    style={{ background: "#25D366" }}
                  >
                    <MessageCircle size={14} /> WhatsApp Yolla
                  </button>
                </div>

                <button
                  onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                  data-testid="paytr-open-link"
                  className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-[--gold] hover:text-[--gold] px-3 py-2.5 text-sm transition"
                >
                  <ExternalLink size={14} /> Kendim Aç (Test)
                </button>
              </div>
            </div>
          )}

          {!loading && !error && url && mode === "iframe" && (
            <div className="relative w-full" style={{ minHeight: 560 }}>
              <iframe
                src={url}
                title="PayTR Güvenli Ödeme"
                data-testid="paytr-iframe"
                className="w-full border border-white/10"
                style={{ height: 620, background: "#fff" }}
                allow="payment; camera"
              />
              <div className="text-[10px] text-neutral-500 tracking-wide mt-2 text-center">
                Kart bilgileri PayTR'de girilir · MİLYEMSOFT hiçbir şekilde saklamaz.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
